/**
 * Minimal markdown renderer for the bounded SITREP template (SITREP.1.5).
 *
 * The SITREP narrative is generated server-side from a fixed template, so we only
 * need to render the small grammar it emits — `#`/`##` headings, `-` bullet
 * lists, `---` rules, blank-line-separated paragraphs, and inline `**bold**` /
 * `*italic*`. Keeping this in-house (rather than adding react-markdown + its
 * transitive deps) honours the lean-infra goal; it is NOT a general-purpose
 * markdown engine and intentionally ignores tables, links, code, images, etc.
 */
import type { JSX } from 'react';

/**
 * Render in-app hash links `[text](#…)` within a text run to anchors, leaving all
 * other text as-is. Bounded on purpose (R5 Slice 4 #514): ONLY same-page hash hrefs
 * (`#incident=<id>` — the SITREP member click-through) become `<a>`; external URLs
 * and any other bracket syntax render as plain text, so the renderer stays the
 * bounded SITREP grammar it was (never a general link engine). Text with no hash
 * link is returned unchanged.
 */
function renderHashLinks(text: string, keyPrefix: string): (JSX.Element | string)[] {
  const re = /\[([^\]]+)\]\((#[^)\s]+)\)/g;
  const out: (JSX.Element | string)[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <a key={`${keyPrefix}-a-${i}`} href={m[2]} className="text-sky-400 hover:underline">
        {m[1]}
      </a>,
    );
    last = m.index + m[0].length;
    i += 1;
  }
  if (out.length === 0) return [text];
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Split a line into inline spans, honouring **bold**, *italic*, and in-app hash
 *  links `[text](#…)` (which may sit inside a bold/italic run). */
function renderInline(text: string, keyPrefix: string): (JSX.Element | string)[] {
  const out: (JSX.Element | string)[] = [];
  // Tokenize on ** or * boundaries. The template never nests the two.
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(...renderHashLinks(text.slice(last, m.index), `${keyPrefix}-t${i}`));
    if (m[2] !== undefined) {
      out.push(
        <strong key={`${keyPrefix}-b-${i}`} className="font-semibold text-slate-100">
          {renderHashLinks(m[2], `${keyPrefix}-b${i}`)}
        </strong>,
      );
    } else if (m[3] !== undefined) {
      out.push(
        <em key={`${keyPrefix}-i-${i}`} className="italic text-slate-400">
          {renderHashLinks(m[3], `${keyPrefix}-i${i}`)}
        </em>,
      );
    }
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(...renderHashLinks(text.slice(last), `${keyPrefix}-tend`));
  return out;
}

/** Render the bounded SITREP markdown to JSX. */
export function renderMarkdown(md: string): JSX.Element {
  const lines = md.split('\n');
  const blocks: JSX.Element[] = [];
  let listBuffer: string[] = [];
  let key = 0;

  const flushList = (): void => {
    if (listBuffer.length === 0) return;
    const items = listBuffer.slice();
    blocks.push(
      <ul key={`ul-${key++}`} className="list-disc pl-5 space-y-0.5 text-slate-300">
        {items.map((item, idx) => (
          <li key={idx}>{renderInline(item, `li-${key}-${idx}`)}</li>
        ))}
      </ul>,
    );
    listBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (line.startsWith('- ')) {
      listBuffer.push(line.slice(2));
      continue;
    }
    flushList();
    if (line === '') {
      continue;
    }
    if (line === '---') {
      blocks.push(<hr key={`hr-${key++}`} className="my-3 border-slate-700" />);
    } else if (line.startsWith('## ')) {
      blocks.push(
        <h2 key={`h2-${key++}`} className="text-sm font-semibold text-slate-100 mt-3 mb-1">
          {renderInline(line.slice(3), `h2-${key}`)}
        </h2>,
      );
    } else if (line.startsWith('# ')) {
      blocks.push(
        <h1 key={`h1-${key++}`} className="text-base font-bold text-slate-50 mb-1">
          {renderInline(line.slice(2), `h1-${key}`)}
        </h1>,
      );
    } else {
      blocks.push(
        <p key={`p-${key++}`} className="text-xs text-slate-300 leading-relaxed">
          {renderInline(line, `p-${key}`)}
        </p>,
      );
    }
  }
  flushList();

  return <div className="space-y-1">{blocks}</div>;
}
